import React from 'react'
import { useMetadataContext } from './Context'
import { Description } from './Description'
import { ButtonGroup } from 'react-bootstrap'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { useToggle } from 'react-use'
import { ImportSpecificationsButton } from './Import/ImportSpecificationsButton'
import { Stack } from 'react-bootstrap'
import { Alert, HorizontalStack, ShowIf, Switch } from '@ztechapps/webui.util'
import { MetadataInput } from './MetadataInput'
import { MetadataDisplayContent } from './MetadataDisplayContent'
import { ExportSpecificationsButton } from './Export/ExportSpecificationsButton'
import { GenerateSpecButton } from './GenerateSpecButton'
import { MaximizeButton } from './MaximizeButton'
import { SpreadsheetMaximizeModal } from './SpreadsheetMaximizeModal'

export const Layout = () => {
  const {
    list,
    isChange,
    hasRole,
    onSave,
    onRevert,
    onUpdateLoading,
    onUpdateError,
    headerName,
    isMaximized,
    showEdit,
    setShowEdit,
    compact,
  } = useMetadataContext()
  const [show, setShow] = useToggle(false)
  const infoLabel = `${!show ? 'Show' : 'Hide'} info`

  // Edit → enter inline edit mode only. The maximize modal stays closed unless the user
  // explicitly clicks the Maximize affordance next to the header. Save / Cancel inside the
  // modal still close the modal AND return to view mode.
  const onEditClick = () => {
    setShowEdit(false)
  }

  // Inline-grid Save / Cancel — used when the user has minimized the modal and is editing
  // inline. Both return the user to view mode (showEdit=true) on completion.
  const onInlineSave = async () => {
    try {
      await onSave()
      setShowEdit(true)
    } catch {
      // Stay in edit mode on error; the <Alert> below renders the error.
    }
  }
  const onInlineCancel = () => {
    onRevert()
    setShowEdit(true)
  }

  return (
    <>
      {/* When `isMaximized` is true, hide the inline scaffold via Bootstrap's `d-none` utility so
          the modal copy has the screen to itself. Hiding (rather than not-rendering) keeps the
          inline subtree mounted so descendant effects and refs survive the toggle. */}
      <div className={isMaximized ? 'd-none' : undefined}>
        <Stack gap={2}>
          <div>
            <HorizontalStack className={'justify-content-between'}>
              <h4 className="m-0">{headerName}</h4>

              <ButtonGroup>
                <Switch expression={!showEdit}>
                  {{
                    true: (
                      <SaveCancelButtons
                        onSave={onInlineSave}
                        onCancel={onInlineCancel}
                        isBusy={onUpdateLoading}
                        disabled={!isChange}
                        saveText={compact ? '' : 'Save'}
                        cancelText={compact ? '' : 'Cancel'}
                        saveBusyText={compact ? '' : undefined}
                      />
                    ),
                    false: (
                      <BusyButton
                        onClick={onEditClick}
                        icon={'edit'}
                        text={compact ? '' : 'Edit'}
                        aria-label={compact ? 'Edit' : undefined}
                        title="Edit"
                        // COUPLING: blocked Edit reason must match EDIT_BLOCKED_REASON in apps/panelpunches/api-test/src/browser/admin/inputs/busy-button-blocked-with-reason.spec.ts
                        disabledReason={!hasRole ? 'Edit — you do not have permission to edit this product' : undefined}
                      />
                    ),
                  }}
                </Switch>
                <ShowIf condition={!showEdit}>
                  <MaximizeButton className="d-none d-md-inline-block" />
                </ShowIf>

                <GenerateSpecButton />
                <ImportSpecificationsButton />
                <ExportSpecificationsButton />
                <BusyButton
                  onClick={setShow}
                  icon={'info'}
                  text={compact ? '' : infoLabel}
                  aria-label={compact ? infoLabel : undefined}
                  title={infoLabel}
                />
              </ButtonGroup>
            </HorizontalStack>
          </div>
          <div>
            <Description show={show} />
          </div>

          {/* Save error surface — footer-level message only; per-cell highlighting is reserved
              for a future backend that returns row indices. */}
          <ShowIf condition={!!onUpdateError}>
            <div>
              <Alert type="danger" icon="exclamation-triangle">
                <span>{onUpdateError?.message ?? 'Failed to save specifications'}</span>
              </Alert>
            </div>
          </ShowIf>

          <div>
            <Switch expression={showEdit}>
              {{
                true: <MetadataDisplayContent value={list} />,
                // When the maximize modal is open, the modal renders its own `<MetadataInput />`.
                // Do NOT render a second inline copy: react-select's menu portals to
                // `document.body`, so even though this outer `<div>` is `d-none`, an open
                // dropdown inside the inline copy stays visible at viewport top-left while the
                // modal's dropdown renders correctly under its input — the user sees two menus.
                // Provider state (list, isChange, activeCell, editingCell) lives in
                // `MetadataContext` so unmounting `<MetadataInput />` here doesn't lose anything;
                // when the modal closes, this branch re-renders and the inline grid comes back.
                false: isMaximized ? null : <MetadataInput />,
              }}
            </Switch>
          </div>
        </Stack>
      </div>

      {/* Modal renders unconditionally; its `show` prop is bound to `isMaximized`. When closed,
          react-bootstrap unmounts the dialog tree, so the second `<MetadataInput />` is unmounted
          with it — dirty state lives in the provider, not inside `<List>`. */}
      <SpreadsheetMaximizeModal />
    </>
  )
}
